import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';

export const LanguageSwitcher: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get language display name
  const getLanguageDisplay = () => {
    switch (i18n.language) {
      case 'vi':
        return 'VI';
      case 'en':
        return 'EN';
      case 'ja':
        return 'JP';
      default:
        return 'VI';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center px-3 py-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
        title={t('language.switchLanguage')}
      >
        <Globe className="w-5 h-5 mr-2" />
        <span className="text-sm font-medium">{getLanguageDisplay()}</span>
        <ChevronDown className="w-4 h-4 ml-1" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg z-50 py-1">
          <button
            onClick={() => changeLanguage('vi')}
            className={`block w-full text-left px-4 py-2 text-sm ${
              i18n.language === 'vi' ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            {t('language.vietnamese')}
          </button>
          <button
            onClick={() => changeLanguage('en')}
            className={`block w-full text-left px-4 py-2 text-sm ${
              i18n.language === 'en' ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            {t('language.english')}
          </button>
          <button
            onClick={() => changeLanguage('ja')}
            className={`block w-full text-left px-4 py-2 text-sm ${
              i18n.language === 'ja' ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            {t('language.japanese')}
          </button>
        </div>
      )}
    </div>
  );
};
